import React, { useState, useEffect } from 'react';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { PListOrderDto } from '../../../../../services/plistOrderService';
import { UserProfileDto } from '../../../../../types/userProfile';

interface DeliveryInfoData {
  totalVolume: string;
  totalWeight: string;
  numOfPackages: string;
  numOfPallets: string;
  carrierName: string;
  packZoneID: string;
  packageRemark: string;
}

interface PListOrderDeliveryData extends DeliveryInfoData {
  orderNo: number;
  expanded: boolean;
}

interface DeliveryInfoModalProps {
  isOpen: boolean;
  pListId: number;
  companyNo: string;
  pListOrders: PListOrderDto[];
  userProfile: UserProfileDto | null;
  onConfirm: (
    pListData: DeliveryInfoData,
    pListOrdersData: Map<number, DeliveryInfoData>
  ) => Promise<void>;
  onClose: () => void;
}

export const DeliveryInfoModal: React.FC<DeliveryInfoModalProps> = ({
  isOpen,
  pListId,
  companyNo,
  pListOrders,
  userProfile,
  onConfirm,
  onClose
}) => {
  const [pListData, setPListData] = useState<DeliveryInfoData>({
    totalVolume: '',
    totalWeight: '',
    numOfPackages: '',
    numOfPallets: '',
    carrierName: '',
    packZoneID: '',
    packageRemark: ''
  });

  const [pListOrdersData, setPListOrdersData] = useState<PListOrderDeliveryData[]>(
    pListOrders.map(order => ({
      orderNo: order.orderNo,
      totalVolume: order.totalVolume?.toString() || '',
      totalWeight: order.totalWeight?.toString() || '',
      numOfPackages: order.numOfPackages?.toString() || '',
      numOfPallets: order.numOfPallets?.toString() || '',
      carrierName: order.carrierName || '',
      packZoneID: order.packZoneID || '',
      packageRemark: order.packageRemark || '',
      expanded: false
    }))
  );

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Sync pListOrdersData when pListOrders prop changes
  useEffect(() => {
    console.log('DeliveryInfoModal received pListOrders:', pListOrders);
    setPListOrdersData(
      pListOrders.map(order => ({
        orderNo: order.orderNo,
        totalVolume: order.totalVolume?.toString() || '',
        totalWeight: order.totalWeight?.toString() || '',
        numOfPackages: order.numOfPackages?.toString() || '',
        numOfPallets: order.numOfPallets?.toString() || '',
        carrierName: order.carrierName || '',
        packZoneID: order.packZoneID || '',
        packageRemark: order.packageRemark || '',
        expanded: false
      }))
    );
  }, [pListOrders]);

  if (!isOpen) return null;

  const handlePListDataChange = (field: keyof DeliveryInfoData, value: string) => {
    setPListData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handlePListOrderDataChange = (orderNo: number, field: keyof DeliveryInfoData, value: string) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, [field]: value }
          : order
      )
    );
  };

  const toggleOrderExpanded = (orderNo: number) => {
    setPListOrdersData(prev =>
      prev.map(order =>
        order.orderNo === orderNo
          ? { ...order, expanded: !order.expanded }
          : order
      )
    );
  };

  const validateRequiredFields = (): string | null => {
    const errors: string[] = [];

    // Check PList required fields
    if (userProfile?.pListWeightRequired && !pListData.totalWeight) {
      errors.push('Vekt er påkrevd');
    }
    if (userProfile?.pListVolumeRequired && !pListData.totalVolume) {
      errors.push('Volum er påkrevd');
    }
    if (userProfile?.pListNumOfPackagesRequired && !pListData.numOfPackages) {
      errors.push('Antall kolli er påkrevd');
    }
    if (userProfile?.pListNumOfPalletsRequired && !pListData.numOfPallets) {
      errors.push('Antall paller er påkrevd');
    }

    if (errors.length > 0) {
      return 'Følgende felter er påkrevd:\n' + errors.join('\n');
    }

    return null;
  };

  const handleSubmit = async () => {
    try {
      // Validate required fields
      const validationError = validateRequiredFields();
      if (validationError) {
        alert(validationError);
        return;
      }

      setIsSubmitting(true);

      // Convert pListOrdersData to Map
      const ordersMap = new Map<number, DeliveryInfoData>();
      pListOrdersData.forEach(order => {
        ordersMap.set(order.orderNo, {
          totalVolume: order.totalVolume,
          totalWeight: order.totalWeight,
          numOfPackages: order.numOfPackages,
          numOfPallets: order.numOfPallets,
          carrierName: order.carrierName,
          packZoneID: order.packZoneID,
          packageRemark: order.packageRemark
        });
      });

      await onConfirm(pListData, ordersMap);
    } catch (error) {
      console.error('Error submitting delivery info:', error);
      alert('Kunne ikke lagre leveranseinformasjon. Prøv igjen.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderDeliveryInfoFields = (
    data: DeliveryInfoData,
    onChange: (field: keyof DeliveryInfoData, value: string) => void,
    readOnly: boolean = false
  ) => (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
      {/* 1. Antall kolli */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Antall kolli{userProfile?.pListNumOfPackagesRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPackagesRequired && !data.numOfPackages ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.numOfPackages}
          onChange={(e) => onChange('numOfPackages', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListNumOfPackagesRequired}
        />
      </div>

      {/* 2. Antall paller */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Antall paller{userProfile?.pListNumOfPalletsRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListNumOfPalletsRequired && !data.numOfPallets ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.numOfPallets}
          onChange={(e) => onChange('numOfPallets', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListNumOfPalletsRequired}
        />
      </div>

      {/* 3. Vekt */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Vekt{userProfile?.pListWeightRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListWeightRequired && !data.totalWeight ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalWeight}
          onChange={(e) => onChange('totalWeight', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListWeightRequired}
        />
      </div>

      {/* 4. Volum */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Volum{userProfile?.pListVolumeRequired && <span className="text-red-600">*</span>}
        </label>
        <input
          type="number"
          step="0.01"
          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none ${
            userProfile?.pListVolumeRequired && !data.totalVolume ? 'border-red-300' : 'border-gray-300'
          }`}
          value={data.totalVolume}
          onChange={(e) => onChange('totalVolume', e.target.value)}
          readOnly={readOnly}
          required={userProfile?.pListVolumeRequired}
        />
      </div>

      {/* 5. Transportør */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Transportør
        </label>
        <input
          type="text"
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          value={data.carrierName}
          onChange={(e) => onChange('carrierName', e.target.value)}
          readOnly={readOnly}
        />
      </div>

      {/* 6 & 7. Pakkesone (kombiboks - skanne eller velge fra dropdown) */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pakkesone
        </label>
        <input
          type="text"
          list={readOnly ? undefined : "packzone-options"}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Skann eller velg pakkesone"
          value={data.packZoneID}
          onChange={(e) => onChange('packZoneID', e.target.value)}
          readOnly={readOnly}
        />
        {!readOnly && (
          <datalist id="packzone-options">
            <option value="ZONE-A" />
            <option value="ZONE-B" />
            <option value="ZONE-C" />
            <option value="ZONE-D" />
            <option value="PAKKING-01" />
            <option value="PAKKING-02" />
          </datalist>
        )}
      </div>

      {/* 8. Pakkemerknad */}
      <div className="col-span-2">
        <label className="block text-sm font-medium text-gray-700 mb-1">
          Pakkemerknad
        </label>
        <textarea
          rows={2}
          className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          placeholder="Skriv inn eventuelle merknader"
          value={data.packageRemark}
          onChange={(e) => onChange('packageRemark', e.target.value)}
          readOnly={readOnly}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-0 sm:p-4 overflow-y-auto">
      <div className="bg-white w-full h-full sm:h-auto sm:rounded-lg sm:max-w-4xl sm:my-8">
        {/* Header */}
        <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-200">
          <h3 className="text-lg sm:text-xl font-semibold">Registrer leveranseinformasjon</h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors min-h-[44px] min-w-[44px] flex items-center justify-center"
          >
            <X size={24} />
          </button>
        </div>

        <div className="p-4 sm:p-6 max-h-[calc(100vh-140px)] sm:max-h-[calc(100vh-200px)] overflow-y-auto">
          {/* PList Section */}
          <div className="mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
              <h4 className="text-lg font-semibold text-blue-900 mb-4">
                Plukkliste #{pListId}
              </h4>
              {renderDeliveryInfoFields(pListData, handlePListDataChange)}
            </div>
          </div>

          {/* PListOrders Section */}
          {pListOrders.length > 0 ? (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Ordrer ({pListOrders.length})
              </h4>
              <p className="text-sm text-gray-600 mb-4">
                Klikk på en ordre for å registrere leveranseinformasjon per ordre.
              </p>

              <div className="space-y-3">
                {pListOrdersData.map((order) => (
                  <div
                    key={order.orderNo}
                    className="border border-gray-200 rounded-lg overflow-hidden"
                  >
                    {/* Order Header */}
                    <button
                      onClick={() => toggleOrderExpanded(order.orderNo)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                    >
                      <span className="font-medium text-gray-900">
                        Ordre #{order.orderNo}
                      </span>
                      {order.expanded ? (
                        <ChevronUp size={20} className="text-gray-600" />
                      ) : (
                        <ChevronDown size={20} className="text-gray-600" />
                      )}
                    </button>

                    {/* Order Content */}
                    {order.expanded && (
                      <div className="p-4 bg-white">
                        {renderDeliveryInfoFields(
                          order,
                          (field, value) => handlePListOrderDataChange(order.orderNo, field, value)
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          ) : (
            <div>
              <h4 className="text-lg font-semibold text-gray-900 mb-3">
                Ordrer (0)
              </h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <p className="text-sm text-gray-600">
                  Ingen ordre-relasjoner funnet for denne plukklisten. Kun plukkliste-informasjon vil bli lagret.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-end gap-3 p-4 sm:p-6 border-t border-gray-200">
          <button
            onClick={onClose}
            className="px-6 py-3 sm:py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors min-h-[44px] order-2 sm:order-1"
            disabled={isSubmitting}
          >
            Avbryt
          </button>
          <button
            onClick={handleSubmit}
            className="px-6 py-3 sm:py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:bg-gray-400 disabled:cursor-not-allowed min-h-[44px] order-1 sm:order-2"
            disabled={isSubmitting}
          >
            {isSubmitting ? 'Lagrer...' : 'Bekreft'}
          </button>
        </div>
      </div>
    </div>
  );
};
